import BaseService from '@/core/services/BaseService';
import Product from '@/domains/Products/Product';
import ProductsDAO from '@/domains/Products/ProductsDAO';
import Core from '@/core/Core';
import ContextMenuItem from "@/lib/components/VContextMenu/ContextMenuItem";
import ExpirationDaysRemaining from "@/domains/Products/ExpirationDaysRemaining";
import RpcDataPagination from '@/core/services/Rpc/RpcDataPagination';

import type RpcDataSort from "@/core/services/Rpc/RpcDataSort";
import ProductActualStatus from "@/domains/Products/ProductActualStatus";
import Dictionary          from "@/core/types/Dictionary";

/**
 * Сервис для работы с продуктами.
 * Публичный API для компонентов: гидрация, уведомления, диалоги.
 */
class ProductsService extends BaseService {

  // ---- Доступность действий ----

  static reasonUnavailableHistory(product: { id: number | null }): { hasReason: boolean; string: string | null } {
    if (product.id) {
      return { hasReason: false, string: null };
    }

    return {
      hasReason: true,
      string:    'Не доступно\nФильтр ещё не создан\n',
    };
  }

  // ---- Справочник остаточных сроков годности ----

  static expirationDaysRemainingList = [
    new ExpirationDaysRemaining(180,  '6 месяцев'),
    new ExpirationDaysRemaining(240,  '8 месяцев'),
    new ExpirationDaysRemaining(365,  '1 год'),
    new ExpirationDaysRemaining(545,  '1,5 года'),
    new ExpirationDaysRemaining(730,  '2 года'),
    new ExpirationDaysRemaining(810,  '2,5 года'),
    new ExpirationDaysRemaining(1095, '3 года'),
  ];

  static getExpirationDateDaysRemainingByDays(daysCount: number | string | null) {
    let days: number | null = null;

    if (typeof daysCount === 'number') {
      days = daysCount;
    } else if (typeof daysCount === 'string') {
      days = parseInt(daysCount, 10);
    }

    return ProductsService.expirationDaysRemainingList.find(item => item.id === days) ?? null;
  }

  /**
   * Кеш статусов актуальности. Наполняется из Dictionaries общим эндпоинтом.
   * reactive — чтобы потребители перерисовывались, когда словарь долетит после логина.
   */
  static productActualStatusList = reactive(new Dictionary<string, ProductActualStatus>());

  /** Статусы актуальности товара */
  static getActualStatusList(): ProductActualStatus[] {
    return this.productActualStatusList.toArray();
  }

  /** Загружает продукт по ID */
  static async get(id: number): Promise<Product | null> {
    const response = await ProductsDAO.get(id);

    return Core.utils.hydrate(response.data, Product.fromServer);
  }

  /** Загружает список продуктов */
  static async getList(params?: Parameters<typeof ProductsDAO.getList>[0]): Promise<Product[]> {
    const response = await ProductsDAO.getList(params);
    return Core.utils.hydrateArray(response.data, Product.fromServer);
  }

  /** Загружает список продуктов с пагинацией (для DataGridLoader) */
  static async getListPaginated(
    params?: Parameters<typeof ProductsDAO.getList>[0] & {
      pagination?: RpcDataPagination;
      sort?: RpcDataSort;
    }
  ): Promise<{ data: Product[]; pagination: RpcDataPagination | null; cancelled: boolean }> {
    const { pagination, sort, ...filters } = params ?? {};
    const response = await ProductsDAO.getList(filters, pagination, sort);

    return {
      data:       Core.utils.hydrateArray(response.data, Product.fromServer),
      pagination: response.meta.pagination,
      cancelled:  response.isCancelled,
    };
  }

  /**
   * Загружает список товаров по артикулу.
   * Бэк возвращает ассоциативный объект { [code]: product } —
   * конвертируем в массив.
   */
  static async getListAsFlatArrayByCode(code: string): Promise<{ data: Product[]; pagination: RpcDataPagination | null; cancelled: boolean }> {
    const response = await ProductsDAO.getList({ code });
    return {
      data:       Object.values(response.data).map(data => Core.utils.hydrate(data, Product.fromServer)),
      pagination: response.meta.pagination,
      cancelled:  response.isCancelled,
    };
  }

  /**
   * Адаптер для DataGridLoader в реестре товаров.
   * Фильтр по code использует отдельный эндпоинт,
   * по причине того, что стандартный getListPaginated
   * при передачи параметра code возвращает ассоциативный массив.
   * Остальные фильтры — стандартный getListPaginated.
   */
  static async getListPaginatedForRegistry(params: Parameters<typeof ProductsService.getListPaginated>[0]): ReturnType<typeof ProductsService.getListPaginated> {
    if (params?.code) {
      return await ProductsService.getListAsFlatArrayByCode(params.code);
    }

    return ProductsService.getListPaginated(params);
  }

  /** Создаёт продукт */
  static async add(product: Product): Promise<boolean> {
    const response = await ProductsDAO.add(product);

    if (response.ok) {
      Core.showNotification.success('Продукт создан');
    }

    return response.ok;
  }

  /** Обновляет продукт */
  static async update(product: Product): Promise<boolean> {
    const response = await ProductsDAO.update(product);

    if (response.ok) {
      Core.showNotification.success('Продукт обновлён');
    }

    return response.ok;
  }

  /** Удаляет продукт */
  static async delete(product: Product): Promise<boolean> {
    const response = await ProductsDAO.delete(product);

    if (response.ok) {
      Core.showNotification.success('Продукт удалён');
    }

    return response.ok;
  }

  /** Удаляет продукт с подтверждением. При успехе помечает продукт удалённым. */
  static async deleteWithConfirmation(product: Product): Promise<boolean> {
    const confirmed = await Core.dialogs.confirm(
      `Удалить ${product.name}?`,
      'Удаление товара',
    );

    if (confirmed === true) {
      const isDeleted = await this.delete(product);

      if (isDeleted === true) {
        product.isDeleted = true;
      }

      return isDeleted;
    }
    else {
      return false;
    }
  }

  /** Поиск продуктов по паттерну */
  static async search(pattern: string): Promise<Product[]> {
    if (!pattern || pattern.length === 0) {
      return [];
    }

    const result = await this.getListPaginated({
      pattern,
      pagination: RpcDataPagination.createForSearch()
    })

    return result.data;
  }

  /**
   * Открывает карточку продукта по id; без id — создание нового.
   * Продукт карточка грузит сама.
   * Промис резолвится по закрытию карточки: true — сохранили, false — закрыли.
   */
  static showCard(id: number | null): Promise<boolean> {
    return Core.dialogs.ProductCard.showAsPromise({ id });
  }

  /** Диалог аналитики закупок номенклатуры. Продавцу не положен — пункт меню ему не показывается */
  static showPurchaseAnalytics(product: Product): Promise<boolean> {
    return Core.dialogs.ProductPurchaseAnalyticsDialog.showAsPromise({ product });
  }

  static get contextMenu(): ContextMenuItem[] {
    return [
      new ContextMenuItem(
        'Открыть',
        'open_in_new',
        (product: Product) => { return this.showCard(product.id); },
        null,
        false,  // hidden
        true,   // dividerAfter
      ),
      new ContextMenuItem(
        'История товара',
        'history',
        (product: Product) => {
          Core.dialogs.FeedDialog.show({
            entityTypeName: 'product',
            entityId:       product.id,
            showAddComment: false,
          });
        },
        (product: Product) => { return ProductsService.reasonUnavailableHistory(product).string; },
      ),
      new ContextMenuItem(
        'Аналитика закупок',
        'show_chart',
        (product: Product) => { return this.showPurchaseAnalytics(product); },
        (product: Product) => {
          if (product.isExisting === true) {
            return null;
          } else {
            return 'Сначала сохраните товар — у нового товара нет ни сделок, ни бюджетных цен';
          }
        },
        () => { return Core.currentPartner?.isBuyer === false; },
      ),
      new ContextMenuItem(
        'Копировать характеристики',
        'copy',
        async (product: Product) => {

          /**
           * В реестре характеристики не приходят (getList без requireNestedObjects).
           * Если их нет — догружаем полный продукт точечно через products.get,
           * который на бэке отдаёт характеристики (requireNestedObjects=true по умолчанию).
           */

          let fullProduct: Product | null;

          if (product.hasCharacteristics()) {
            fullProduct = product;
          }
          else {
            fullProduct = await this.get(product.id);
          }

          if (Core.utils.isNullOrUndefined(fullProduct) === true) {
            // продукт не загрузился — копировать нечего
          }
          else {
            const isCopied = await Core.utils.copyHtmlToClipboard(fullProduct.copyProductCharacteristics());

            if (isCopied === true) {
              Core.showNotification.success('Скопировано в буфер обмена!');
            } else {
              Core.showNotification.error('Ошибка при копировании');
            }
          }
        },
        null,
        false,  // hidden
        true,   // dividerAfter
      ),
      new ContextMenuItem(
        'Удалить',
        'delete',
        (product: Product) => { return this.deleteWithConfirmation(product); },
      ),
    ];
  }
}

export default ProductsService;